import { type ProductCardData } from "@faire/prototype-playground/components/shared/brand-home-framework/ProductCard";
import { BestsellersRow } from "@faire/prototype-playground/components/shared/brand-home-framework/sections/BestsellersRow";
import { CollectionsRow } from "@faire/prototype-playground/components/shared/brand-home-framework/sections/CollectionsRow";
import { EditorialBanner } from "@faire/prototype-playground/components/shared/brand-home-framework/sections/EditorialBanner";
import { getProductsByBrandToken } from "@faire/prototype-playground/data/get_entity";
import { cn } from "@faire/prototype-playground/lib/utils";
import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import { Button } from "@faire/slate/components/button";
import React, { useEffect, useRef, useState } from "react";

import {
  BESTSELLER_KEYWORDS,
  CREAM,
  MAROON,
  PADX,
  PF_BRAND_TOKEN,
  PROMO_IMAGE,
  categoryLabelForCollection,
  categoryLabelForProductName,
  getBrandDataset,
  readToolbarSettings,
  writeToolbarSettings,
} from "./data";

// The brand's own "Register on Faire" router page (register-faire-v4 layout,
// with the International Distributors section + modal), served as a static
// capture from public/. Its "Browse on Faire" CTAs are repointed to
// /rl/pfcandle-home-v2 and open in the top window.
const ENTRY_PAGE_URL =
  "/prototypes/pf-candle-co/pfcandle-register-faire-v2.html";

// Build a link from the brand.com surface into Faire. `ref` is the referral
// signal (a category the shopper engaged with) that up-sorts the Faire grid.
// Matches the captured page's own CTAs: land logged-out (auth:2) + arrive=1.
const faireHref = (ref?: string): string =>
  `/rl/pfcandle-home-v2?variants=auth:2&arrive=1${
    ref ? `&ref=${encodeURIComponent(ref)}` : ""
  }`;

export const EntryPage: React.FC = () => {
  // The captured router page renders in normal flow (not a fixed overlay) so the
  // new merchandising below it scrolls as one continuous surface. We size the
  // iframe to its content height (same-origin, so we can read scrollHeight).
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [frameHeight, setFrameHeight] = useState<number>(0);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const timers: ReturnType<typeof setTimeout>[] = [];
    let rafId = 0;

    const measure = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;
      const height = Math.max(
        doc.body?.scrollHeight ?? 0,
        doc.documentElement?.scrollHeight ?? 0
      );
      // Only update when the height actually changes.
      setFrameHeight((prev) => (height > 0 && height !== prev ? height : prev));
    };
    const scheduleMeasure = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };

    const handleLoad = () => {
      measure();
      // Re-measure a few times as late webfont / image reflow grows the page —
      // simpler and quieter than a ResizeObserver (which trips the browser's
      // benign "ResizeObserver loop" warning against the dev error overlay).
      [150, 400, 800, 1500, 3000].forEach((delay) =>
        timers.push(setTimeout(measure, delay))
      );
    };

    iframe.addEventListener("load", handleLoad);
    window.addEventListener("resize", scheduleMeasure);
    // If the capture is already cached and loaded, measure immediately.
    if (iframe.contentDocument?.readyState === "complete") handleLoad();

    return () => {
      iframe.removeEventListener("load", handleLoad);
      window.removeEventListener("resize", scheduleMeasure);
      if (rafId) cancelAnimationFrame(rafId);
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  // "Faire landing" toggle — persisted so the choice made here is what the
  // shopper lands on when they click into Faire (Page.tsx reads the same store).
  const [landing, setLanding] = useState<number>(
    () => readToolbarSettings().landing ?? 1
  );
  const handleLanding = (next: number) => {
    setLanding(next);
    writeToolbarSettings({ landing: next });
  };

  // Real P.F. Candle Co. catalog (same entity source the Faire home uses).
  const dataset = getBrandDataset(1);
  const products = getProductsByBrandToken(PF_BRAND_TOKEN).map((product) => {
    const lower = product.name.toLowerCase();
    return {
      ...product,
      isBestseller:
        product.isBestseller ||
        BESTSELLER_KEYWORDS.some((keyword) => lower.includes(keyword)),
    };
  });

  const toCard = (product: (typeof products)[number]): ProductCardData => ({
    token: product.token,
    name: product.name,
    imageUrl: product.imageUrl,
    priceDollars: product.priceDollars,
    msrpDollars: product.msrpDollars,
    caseSize: product.caseSize,
    isBestseller: product.isBestseller,
  });

  const bestsellerCards = products
    .filter((product) => product.isBestseller)
    .slice(0, 8)
    .map(toCard);
  const moreCards = products
    .filter((product) => !product.isBestseller)
    .slice(0, 8)
    .map(toCard);

  const productLink = (card: ProductCardData): string =>
    faireHref(categoryLabelForProductName(card.name));

  return (
    <div className="bg-fs-surface-primary">
      <LiveToolbar
        variantGroups={[
          {
            title: "Faire landing",
            queryKey: "landing",
            active: landing,
            onChange: handleLanding,
            variants: [
              { variantNumber: 1, label: "FD homepage" },
              { variantNumber: 2, label: "All-products grid" },
            ],
          },
        ]}
      />

      {/* Top: the brand's real captured router page. */}
      <iframe
        ref={iframeRef}
        src={ENTRY_PAGE_URL}
        title="P.F. Candle Co. — Register on Faire"
        scrolling="no"
        style={{
          display: "block",
          width: "100%",
          height: frameHeight ? `${frameHeight}px` : "100vh",
          border: "none",
        }}
      />

      {/* Below: the new shoppable-looking merchandising — visually rich but not
          shoppable (no prices); every element routes into Faire. */}
      <section>
        <div
          className={cn("py-10 text-center", PADX)}
          style={{ backgroundColor: MAROON }}
        >
          <p className="type-fs-display-s" style={{ color: CREAM }}>
            Preview our wholesale catalog on Faire
          </p>
          <p
            className="type-fs-paragraph mx-auto mt-2 max-w-[580px]"
            style={{ color: CREAM }}
          >
            Browse our bestsellers and collections below. Sign in on Faire to
            see wholesale pricing, unlock net-60 terms, and place your order.
          </p>
        </div>

        <EditorialBanner
          href={faireHref()}
          imageUrl={PROMO_IMAGE}
          lineOne="Poured in California"
          lineTwo="100% soy wax candles"
          ctaLabel="Shop Candles"
        />
        <BestsellersRow
          products={bestsellerCards}
          priceMode="none"
          title="Bestsellers"
          linkFor={productLink}
        />
        <CollectionsRow
          collections={dataset.collections}
          title="Shop our collections"
          linkFor={(collection) =>
            faireHref(categoryLabelForCollection(collection.label))
          }
        />
        <BestsellersRow
          products={moreCards}
          priceMode="none"
          title="More from P.F. Candle Co."
          linkFor={productLink}
        />

        <div className={cn("flex justify-center pt-2 pb-16", PADX)}>
          <Button
            variant="primary"
            onClick={() => {
              window.location.href = faireHref();
            }}
          >
            Shop the full catalog on Faire
          </Button>
        </div>
      </section>
    </div>
  );
};
